import React, { useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Radio,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WalletIcon from "@mui/icons-material/Wallet";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CircularProgress from "@mui/material/CircularProgress";

import { useSelector } from "react-redux";
import {
  apspdcl_callbackurl,
  entity_id,
  locationGeo,
  Platform,
} from "../Utils/Const";
import { baseapi, frontbaseapi, paymentbaseapi } from "../Environmentalurl";
import getDeviceId from "../Utils/useIPAddress";
import api from "../Utils/Api";

// ======================================================
// PAYMENT METHODS
// ======================================================

// The payment gateway amount is calculated dynamically below.
// It is always: Bill Amount - TA Wallet Balance.
// Example: bill = 249, wallet = 79 => load = 170.

const paymentMethods = [
  {
    id: "credit",
    label: "Credit Card",
    icon: <CreditCardIcon />,
  },
  {
    id: "debit",
    label: "Debit Card",
    icon: <CreditCardIcon />,
  },
  {
    id: "netbanking",
    label: "Internet Banking",
    icon: <AccountBalanceIcon />,
  },
  {
    id: "wallet",
    label: "Wallet/ Cash Cards",
    icon: <WalletIcon />,
  },
];

// ======================================================
// MAIN COMPONENT
// ======================================================

const TAWalletPayment = () => {
  const loginMeta = useSelector((state) => state.auth.loginMeta);
  const reduxUser = useSelector((state) => state.auth.user);
  const location = useLocation();
  const routeState = location.state || {};

  // The login page passes the EXACT OrderID that was used by
  // apspdclbillpayment. Persist that value before reading orderId.
  const routeOrderId =
    routeState?.orderId ||
    routeState?.OrderID ||
    routeState?.apspdclPaymentRequest?.OrderID ||
    "";

  if (routeOrderId) {
    sessionStorage.setItem("taWalletTransactionOrderId", String(routeOrderId));
  }

  const [selectedMethod, setSelectedMethod] = useState("credit");

  const [cardNumber, setCardNumber] = useState("");
  const [cardHolderName, setCardHolderName] = useState("");
  // Single expiry field: MM/YY. "/" is inserted after the month.
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");

  const [selectedBank, setSelectedBank] = useState("");
  const [wallet, setWallet] = useState("");

  const [cardInfo, setCardInfo] = useState(null);
  const [cardType, setCardType] = useState("");
  const [isCardLookupLoading, setIsCardLookupLoading] = useState(false);
  const [lastFetchedBin, setLastFetchedBin] = useState("");

  const [errors, setErrors] = useState({});
  const [isPaying, setIsPaying] = useState(false);

  const ip = getDeviceId();
  // --------------------------------------------------
  // Transaction values
  // --------------------------------------------------

  // Mobile must come from the transaction session.
  const mobileNumber =
    sessionStorage.getItem("taWalletMobile") ||
    sessionStorage.getItem("MobileNumber") ||
    reduxUser?.MobileNumber ||
    "";

  // EXACT same OrderID used by apspdclbillpayment.
  // Route state and Redux are fallbacks only.
  const persistedApspdclOrderId = (() => {
    try {
      const raw = sessionStorage.getItem("taWalletApspdclPaymentRequest") || "";

      if (!raw) {
        return "";
      }

      const parsed = JSON.parse(raw);

      return parsed?.OrderID ? String(parsed.OrderID) : "";
    } catch {
      return "";
    }
  })();

  const orderId =
    routeOrderId ||
    sessionStorage.getItem("taWalletTransactionOrderId") ||
    persistedApspdclOrderId ||
    loginMeta?.orderId ||
    loginMeta?.OrderID ||
    "";

  if (orderId) {
    sessionStorage.setItem("taWalletTransactionOrderId", String(orderId));
  }

  // Read the OTP-login response/profile if the login page stored it.
  // The helper also supports the common session keys used by the flow.
  const readOtpProfile = () => {
    const keys = [
      "taWalletOtpResponse",
      "taWalletLoginResponse",
      "taWalletLoginData",
      "loginValidateOtpResponse",
    ];

    for (const key of keys) {
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) continue;

        const parsed = JSON.parse(raw);
        const info = Array.isArray(parsed?.Info)
          ? parsed.Info[0]
          : parsed?.Info;

        let customerProfile = info?.CustomerProfile;

        if (typeof customerProfile === "string") {
          customerProfile = JSON.parse(customerProfile);
        }

        if (Array.isArray(customerProfile)) {
          customerProfile = customerProfile[0];
        }

        if (customerProfile) {
          return {
            response: parsed,
            profile: customerProfile,
          };
        }
      } catch {
        // Try the next possible session key.
      }
    }

    return {
      response: null,
      profile: null,
    };
  };

  const { profile: otpProfile } = readOtpProfile();

  // ==================================================
  // CURRENT TRANSACTION AMOUNTS
  // ==================================================
  // The TPIN login page passes billAmount and walletBalance in
  // React Router state. Those values belong to THIS transaction,
  // so they must be preferred over older Redux/session values.

  const routeBillAmount = Number(routeState?.billAmount);
  const routeWalletBalance = Number(routeState?.walletBalance);

  // Persist the current transaction values so the same values are
  // available if the user refreshes the payment page.
  if (
    routeState?.billAmount !== undefined &&
    Number.isFinite(routeBillAmount) &&
    routeBillAmount >= 0
  ) {
    sessionStorage.setItem("taWalletBillAmount", String(routeBillAmount));

    sessionStorage.setItem("billAmount", String(routeBillAmount));

    sessionStorage.setItem("apspdclBillAmount", String(routeBillAmount));
  }

  if (
    routeState?.walletBalance !== undefined &&
    Number.isFinite(routeWalletBalance) &&
    routeWalletBalance >= 0
  ) {
    sessionStorage.setItem("taWalletBalance", String(routeWalletBalance));
  }

  const sessionBillAmount =
    Number(sessionStorage.getItem("taWalletBillAmount")) ||
    Number(sessionStorage.getItem("billAmount")) ||
    Number(sessionStorage.getItem("apspdclBillAmount")) ||
    0;

  const sessionWalletBalance =
    Number(sessionStorage.getItem("taWalletBalance")) || 0;

  // Prefer the values passed from the current TPIN transaction.
  const billAmount =
    routeState?.billAmount !== undefined && Number.isFinite(routeBillAmount)
      ? routeBillAmount
      : Number(loginMeta?.amount) || sessionBillAmount || 0;

  const walletBalance =
    routeState?.walletBalance !== undefined &&
    Number.isFinite(routeWalletBalance)
      ? routeWalletBalance
      : Number(otpProfile?.Balance) ||
        Number(loginMeta?.walletBalance) ||
        sessionWalletBalance ||
        0;

  // ==================================================
  // LOAD / PAYMENT AMOUNT
  // ==================================================
  // This is the amount that must be paid through the payment gateway.
  // It is NOT the full bill amount and NOT the wallet balance.
  //
  // Example:
  // Bill Amount    = 249
  // Wallet Balance = 79
  // Load Amount    = 249 - 79 = 170
  //
  // If wallet balance is greater than the bill, the load amount is 0.
  const paymentAmount = Math.max(Number(billAmount) - Number(walletBalance), 0);

  // Keep the calculated amount available to the payment flow after
  // refresh/navigation.
  sessionStorage.setItem("taWalletPaymentAmount", String(paymentAmount));

  console.log("TA Wallet payment calculation:", {
    billAmount,
    walletBalance,
    paymentAmount,
  });

  const email =
    otpProfile?.Poiemail ||
    reduxUser?.Poiemail ||
    sessionStorage.getItem("taWalletEmail") ||
    sessionStorage.getItem("Email") ||
    "sample@example.com";

  const address1 = otpProfile?.Poastate || reduxUser?.Poastate || "";

  const address2 = otpProfile?.Poavtc || reduxUser?.Poavtc || "";

  const city = otpProfile?.Poadist || reduxUser?.Poadist || "";

  // --------------------------------------------------
  // Card BIN / first-six-digits lookup
  // --------------------------------------------------

  const fetchCardInfo = async (bin) => {
    if (!bin || bin.length !== 6) return;

    setIsCardLookupLoading(true);

    try {
      // IMPORTANT: this must be the SessionID returned by
      // loginwithtpin, not the SessionID from OTP validation.
      const sessionID =
        sessionStorage.getItem("taWalletSessionID") ||
        sessionStorage.getItem("SessionID") ||
        "";

      if (!sessionID) {
        setCardInfo(null);
        setCardType("");
        setErrors((prev) => ({
          ...prev,
          general: "Wallet Session ID is missing. Please login again.",
        }));
        return;
      }

      const payload = {
        CardNumber: bin,
        EntityID: entity_id,
        SessionID: sessionID,
        Mobilenumber: mobileNumber,
        MachineID: ip,
        GeoLocation: locationGeo,
        ReqType: "TA_Get_PayUCardInfo",
        Platform: Platform,
      };

      // Use the application's api wrapper here. It applies the
      // normal API authentication/interceptor used by the project.
      // The SessionID is still explicitly sent in the request body.
      const response = await api.post(`${baseapi}/auth/Cardinfo`, payload);

      const data = response?.data;

      if (data?.Response_Code === "2980" && data?.Info) {
        setCardInfo(data.Info);

        const returnedCategory = String(
          data.Info.cardCategory || "",
        ).toUpperCase();

        setCardType(String(data.Info.cardType || "").toUpperCase());

        // Keep credit/debit selection aligned with
        // the API response.
        if (returnedCategory === "CC") {
          setSelectedMethod("credit");
        } else if (returnedCategory === "DC") {
          setSelectedMethod("debit");
        }

        // Clear any previous BIN error after success.
        setErrors((prev) => {
          const next = { ...prev };
          delete next.general;
          return next;
        });
      } else {
        setCardInfo(null);
        setCardType("");
        setErrors((prev) => ({
          ...prev,
          general:
            data?.Message ||
            data?.message ||
            "Unable to fetch card information.",
        }));
      }
    } catch (error) {
      console.error("TA_Get_PayUCardInfo failed:", error);

      setCardInfo(null);
      setCardType("");

      const status = error?.response?.status;

      const message =
        error?.response?.data?.Message || error?.response?.data?.message;

      if (status === 401) {
        setErrors((prev) => ({
          ...prev,
          general:
            message || "TA Wallet session is unauthorized. Please login again.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general:
            message || "Unable to fetch card information. Please try again.",
        }));
      }
    } finally {
      setIsCardLookupLoading(false);
    }
  };

  // --------------------------------------------------
  // Card number change
  // --------------------------------------------------

  const handleCardNumberChange = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 16);

    setCardNumber(formatCardNumber(digits));

    if (digits.length < 6) {
      setCardInfo(null);
      setCardType("");
      setLastFetchedBin("");
      return;
    }

    // Call the BIN API immediately when the 6th digit is entered.
    // If the user changes any of the first 6 digits, call it again.
    const bin = digits.slice(0, 6);
    if (bin.length === 6 && bin !== lastFetchedBin) {
      setLastFetchedBin(bin);
      setCardInfo(null);
      setCardType("");
      fetchCardInfo(bin);
    }
  };

  // --------------------------------------------------
  // CHANGE PAYMENT METHOD
  // --------------------------------------------------

  const handleMethodChange = (method) => {
    setSelectedMethod(method);
    setErrors({});
  };

  // --------------------------------------------------
  // MAKE PAYMENT
  // --------------------------------------------------

  const handlePayment = (event) => {
    event?.preventDefault();

    console.log("=================================");
    console.log("MAKE PAYMENT CLICKED");
    console.log("=================================");

    const tempErrors = {};

    // Resolve the EXACT OrderID used by apspdclbillpayment.
    const finalOrderId =
      routeOrderId ||
      sessionStorage.getItem("taWalletTransactionOrderId") ||
      persistedApspdclOrderId ||
      String(orderId || "");

    const cleanCardNumber = cardNumber ? cardNumber.replace(/\s+/g, "") : "";

    console.log("OrderID:", finalOrderId || "(missing)");
    console.log("Payment method:", selectedMethod);
    console.log("Payment amount:", paymentAmount);
    console.log("Card type:", cardType || "(missing)");

    // --------------------------------------------
    // VALIDATION
    // --------------------------------------------

    if (!mobileNumber) {
      tempErrors.mobile =
        "Mobile number not found. Please start the transaction again.";
    }

    if (!paymentAmount || Number(paymentAmount) <= 0) {
      tempErrors.amount = "Payment amount is not available.";
    } else if (Number(paymentAmount) < 10) {
      tempErrors.amount = "Amount must be at least Rs. 10.";
    }

    if (!finalOrderId) {
      tempErrors.general =
        "Transaction Order ID is missing. Please start the transaction again.";
    }

    if (selectedMethod === "credit" || selectedMethod === "debit") {
      if (!cleanCardNumber || cleanCardNumber.length !== 16) {
        tempErrors.cardNumber = "Enter a valid 16-digit card number.";
      }

      if (!expiryDate || !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiryDate)) {
        tempErrors.expiry = "Enter valid expiry (MM/YY).";
      }

      if (!cvv || cvv.length < 3) {
        tempErrors.cvv = "Enter a valid 3-digit CVV.";
      }

      if (!cardHolderName?.trim()) {
        tempErrors.nameOnCard = "Name on card is required.";
      }

      const effectiveCardType =
        cardType ||
        (String(cardInfo?.cardCategory || "").toUpperCase() === "CC"
          ? "CC"
          : String(cardInfo?.cardCategory || "").toUpperCase() === "DC"
            ? "DC"
            : selectedMethod === "credit"
              ? "CC"
              : "DC");

      if (!effectiveCardType) {
        tempErrors.cardNumber =
          "Unable to determine card type. Please enter the card number again.";
      }
    }

    if (selectedMethod === "netbanking") {
      tempErrors.general =
        "Internet Banking gateway integration is not available in the supplied API flow.";
    }

    if (selectedMethod === "wallet") {
      tempErrors.general =
        "Wallet / Cash Card gateway integration is not available in the supplied API flow.";
    }

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length > 0) {
      console.log("PAYMENT STOPPED - VALIDATION ERRORS:", tempErrors);
      return;
    }

    // --------------------------------------------
    // KEEP EXACT ORDER ID
    // --------------------------------------------

    sessionStorage.setItem("taWalletTransactionOrderId", String(finalOrderId));

    // tapay_cc_submit expects MM/YYYY.
    const [expiryMonthValue, expiryYearValue] = expiryDate.split("/");

    const formattedExpiry = `${expiryMonthValue}/20${expiryYearValue}`;

    // Cardinfo normally returns cardType. If it returns only the
    // category, support both payment methods: CC = Credit Card,
    // DC = Debit Card.
    const gatewayCardType =
      cardType ||
      (String(cardInfo?.cardCategory || "").toUpperCase() === "CC"
        ? "CC"
        : String(cardInfo?.cardCategory || "").toUpperCase() === "DC"
          ? "DC"
          : selectedMethod === "credit"
            ? "CC"
            : "DC");

    // --------------------------------------------
    // EXACT TAPAY PARAMETERS
    // Same flow as Loadusingccdc.
    // --------------------------------------------

    const params = {
      mobileNum: mobileNumber,
      amount: paymentAmount,
      cardType: gatewayCardType,
      cardNumber: cleanCardNumber,
      expiry_date: formattedExpiry,
      cvv: cvv,
      card_holder_name: cardHolderName,
      returnUrl: `${frontbaseapi}/apspdcl`,
      orderID: finalOrderId,
      email: email,
      address1: address1,
      address2: address2,
      city: city,
      responseurl: apspdcl_callbackurl,
    };

    console.log("=================================");
    console.log("TAPAY CC SUBMIT");
    console.log("=================================");
    console.log("URL:", `${paymentbaseapi}/seemless/enc/tapay_cc_submit`);
    console.log("OrderID:", finalOrderId);
    console.log("Amount:", paymentAmount);
    console.log("CardType:", gatewayCardType);

    // Do not log card number or CVV.

    try {
      sessionStorage.setItem(
        "tapayAttempt",
        JSON.stringify({
          orderId: String(finalOrderId),
          at: Date.now(),
        }),
      );
    } catch {
      // Non-fatal.
    }

    // --------------------------------------------
    // SUBMIT EXACTLY LIKE Loadusingccdc
    // --------------------------------------------

    try {
      setIsPaying(true);

      const form = document.createElement("form");

      form.method = "GET";

      form.action = `${paymentbaseapi}/seemless/enc/tapay_cc_submit`;

      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement("input");

        input.type = "hidden";
        input.name = key;
        input.value =
          value === undefined || value === null ? "" : String(value);

        form.appendChild(input);
      });

      console.log("Form created. Submitting tapay_cc_submit...");

      document.body.appendChild(form);

      form.submit();
    } catch (error) {
      console.error("tapay_cc_submit submission failed:", error);

      setIsPaying(false);

      setErrors((prev) => ({
        ...prev,
        general:
          error?.message || "Unable to process the payment. Please try again.",
      }));
    }
  };

  // --------------------------------------------------
  // CANCEL
  // --------------------------------------------------

  const handleCancel = () => {
    window.location.href = "/ta-wallet-electricity";
  };

  // ==================================================
  // RENDER PAYMENT CONTENT
  // ==================================================

  const renderPaymentContent = () => {
    switch (selectedMethod) {
      case "credit":
        return (
          <CreditCardForm
            cardNumber={cardNumber}
            setCardNumber={setCardNumber}
            cardHolderName={cardHolderName}
            setCardHolderName={setCardHolderName}
            expiryDate={expiryDate}
            setExpiryDate={setExpiryDate}
            cvv={cvv}
            setCvv={setCvv}
            errors={errors}
            onCardNumberChange={handleCardNumberChange}
            cardType={cardType}
            cardInfo={cardInfo}
            isCardLookupLoading={isCardLookupLoading}
          />
        );

      case "debit":
        return (
          <DebitCardForm
            cardNumber={cardNumber}
            setCardNumber={setCardNumber}
            cardHolderName={cardHolderName}
            setCardHolderName={setCardHolderName}
            expiryDate={expiryDate}
            setExpiryDate={setExpiryDate}
            cvv={cvv}
            setCvv={setCvv}
            errors={errors}
            onCardNumberChange={handleCardNumberChange}
            cardType={cardType}
            cardInfo={cardInfo}
            isCardLookupLoading={isCardLookupLoading}
          />
        );

      case "netbanking":
        return (
          <NetBankingForm
            selectedBank={selectedBank}
            setSelectedBank={setSelectedBank}
          />
        );

      case "wallet":
        return <WalletForm wallet={wallet} setWallet={setWallet} />;

      default:
        return null;
    }
  };

  // ==================================================
  // MAIN UI
  // ==================================================

  return (
    <Box
      sx={{
        minHeight: "80vh",
        width: "100%",
        backgroundColor: "#ffffff",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: {
          xs: 3,
          md: 7,
        },
        pb: 5,
        px: 2,
        boxSizing: "border-box",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      {/* ==================================================
                PAYMENT GATEWAY CONTAINER
            ================================================== */}

      <Box
        sx={{
          width: "100%",
          maxWidth: 1160,
          minHeight: {
            xs: "auto",
            md: 550,
          },
          border: "1px solid #d0d0d0",
          backgroundColor: "#ffffff",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "265px 1fr",
          },
          position: "relative",
        }}
      >
        {/* ==================================================
                    LEFT PAYMENT MENU
                ================================================== */}

        <Box
          sx={{
            backgroundColor: "#eeeeee",
            borderRight: {
              xs: "none",
              md: "1px solid #d2d2d2",
            },
            borderBottom: {
              xs: "1px solid #d2d2d2",
              md: "none",
            },
          }}
        >
          {paymentMethods.map((method) => {
            const active = selectedMethod === method.id;

            return (
              <PaymentMenuItem
                key={method.id}
                method={method}
                active={active}
                onClick={() => handleMethodChange(method.id)}
              />
            );
          })}
        </Box>

        {/* ==================================================
                    RIGHT CONTENT
                ================================================== */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 285px",
            },
            minWidth: 0,
          }}
        >
          {/* ==================================================
                        CENTER FORM
                    ================================================== */}

          <Box
            sx={{
              px: {
                xs: 2,
                sm: 3,
                md: 3,
              },
              py: {
                xs: 2.5,
                md: 3,
              },
              minWidth: 0,
            }}
          >
            {renderPaymentContent()}

            {/* ==================================================
                            MAKE PAYMENT
                        ================================================== */}

            {errors.general && (
              <Typography
                color="error"
                sx={{
                  mt: 2,
                  fontSize: 14,
                }}
              >
                {errors.general}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handlePayment}
              sx={{
                mt: 4,
                height: 53,
                borderRadius: "3px",
                background: "linear-gradient(#ffe08a, #f5c64d)",
                border: "1px solid #d1a63a",
                color: "#000000",
                fontSize: 18,
                fontWeight: 400,
                textTransform: "none",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",

                "&:hover": {
                  background: "linear-gradient(#ffdf82, #f2c045)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                },
              }}
            >
              {isPaying
                ? "Processing..."
                : `Make Payment ₹${Number(paymentAmount || 0).toFixed(2)}`}
            </Button>

            {/* ==================================================
                            CANCEL
                        ================================================== */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={handleCancel}
                sx={{
                  minWidth: 0,
                  px: 0,
                  mt: 0.3,
                  color: "#0066b3",
                  fontSize: 16,
                  textTransform: "none",
                  fontWeight: 400,

                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>

          {/* ==================================================
                        MERCHANT SUMMARY
                    ================================================== */}

          <MerchantSummary paymentAmount={paymentAmount} />
        </Box>

        {/* ==================================================
                    BILLDESK BRAND
                ================================================== */}

        <Box
          sx={{
            position: {
              xs: "static",
              md: "absolute",
            },
            right: {
              md: 20,
            },
            bottom: {
              md: 14,
            },
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: {
              xs: 2,
              md: 0,
            },
            gridColumn: {
              xs: "1 / -1",
              md: "auto",
            },
          }}
        >
          <Box
            component="img"
            src="/assets/tacircle_logo.png"
            alt="Company Logo"
            sx={{
              width: 100,
              height: 80,
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ======================================================
// PAYMENT MENU ITEM
// ======================================================

const PaymentMenuItem = ({ method, active, onClick }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        height: {
          xs: 58,
          md: 54,
        },
        px: 2.5,
        display: "flex",
        alignItems: "center",
        position: "relative",
        cursor: "pointer",
        borderBottom: "1px solid #d4d4d4",

        backgroundColor: active ? "#f9f9f9" : "#eeeeee",

        transition: "background-color 0.15s",

        "&:hover": {
          backgroundColor: "#f7f7f7",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          color: active ? "#333333" : "#444444",
        }}
      >
        {React.cloneElement(method.icon, {
          sx: {
            fontSize: 25,
            color: "#444444",
          },
        })}

        <Typography
          sx={{
            fontSize: 16,
            fontWeight: active ? 600 : 400,
            color: "#222222",
            whiteSpace: "nowrap",
          }}
        >
          {method.label}
        </Typography>
      </Box>

      {active && (
        <ArrowForwardIosIcon
          sx={{
            position: "absolute",
            right: 8,
            fontSize: 20,
            color: "#111111",
          }}
        />
      )}
    </Box>
  );
};

// ======================================================
// CREDIT CARD FORM
// ======================================================

const CreditCardForm = ({
  cardNumber,
  setCardNumber,
  cardHolderName,
  setCardHolderName,
  expiryDate,
  setExpiryDate,
  cvv,
  setCvv,
  errors,
  onCardNumberChange,
  cardType,
  cardInfo,
  isCardLookupLoading,
}) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 18,
          color: "#000000",
          mb: 1.5,
          borderBottom: "2px solid #16834d",
          display: "inline-block",
          pb: 1,
          pr: 3,
        }}
      >
        Pay by Credit Card
      </Typography>

      <CardBrands type="credit" />

      {/* CARD NUMBER */}

      <Typography sx={fieldLabelStyle}>Card Number</Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Enter card number"
        value={cardNumber}
        onChange={(e) => onCardNumberChange(e.target.value)}
        inputProps={{
          maxLength: 19,
          inputMode: "numeric",
        }}
        InputProps={{
          endAdornment: isCardLookupLoading ? (
            <CircularProgress size={18} />
          ) : cardType ? (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#16834d",
              }}
            >
              {cardType}
            </Typography>
          ) : null,
        }}
        sx={paymentTextField}
      />

      {/* EXPIRATION DATE */}

      <Box sx={{ mt: 2 }}>
        <Typography sx={fieldLabelStyle}>Expiration Date</Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="MM/YY"
          value={expiryDate}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 4);

            let formatted = digits;

            if (digits.length > 2) {
              formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }

            setExpiryDate(formatted);
          }}
          error={Boolean(errors?.expiry)}
          helperText={errors?.expiry || "Enter month and year, e.g. 01/28"}
          inputProps={{
            maxLength: 5,
            inputMode: "numeric",
          }}
          sx={paymentTextField}
        />
      </Box>

      {/* CARD HOLDER NAME */}

      <Typography
        sx={{
          ...fieldLabelStyle,
          mt: 2,
        }}
      >
        Card Holder Name
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Enter card holder name"
        value={cardHolderName}
        onChange={(e) => setCardHolderName(e.target.value)}
        sx={paymentTextField}
      />

      {/* CVV */}

      <Typography
        sx={{
          ...fieldLabelStyle,
          mt: 2,
        }}
      >
        CVV/CVC
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Enter CVV"
        value={cvv}
        onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
        error={Boolean(errors?.cvv)}
        helperText={errors?.cvv}
        inputProps={{
          maxLength: 4,
          inputMode: "numeric",
        }}
        sx={paymentTextField}
      />
    </Box>
  );
};

// ======================================================
// DEBIT CARD FORM
// ======================================================

const DebitCardForm = ({
  cardNumber,
  setCardNumber,
  cardHolderName,
  setCardHolderName,
  expiryDate,
  setExpiryDate,
  cvv,
  setCvv,
  errors,
  onCardNumberChange,
  cardType,
  cardInfo,
  isCardLookupLoading,
}) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 20,
          color: "#000000",
          mb: 2,
        }}
      >
        Pay using Debit Card
      </Typography>

      <CardBrands type="debit" />

      <Typography sx={fieldLabelStyle}>Card Number</Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Enter card number"
        value={cardNumber}
        onChange={(e) => onCardNumberChange(e.target.value)}
        inputProps={{
          maxLength: 19,
          inputMode: "numeric",
        }}
        InputProps={{
          endAdornment: isCardLookupLoading ? (
            <CircularProgress size={18} />
          ) : cardType ? (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#16834d",
              }}
            >
              {cardType}
            </Typography>
          ) : null,
        }}
        sx={paymentTextField}
      />

      {/* EXPIRATION DATE */}

      <Box sx={{ mt: 2 }}>
        <Typography sx={fieldLabelStyle}>Expiration Date</Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="MM/YY"
          value={expiryDate}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 4);

            let formatted = digits;

            if (digits.length > 2) {
              formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }

            setExpiryDate(formatted);
          }}
          error={Boolean(errors?.expiry)}
          helperText={errors?.expiry || "Enter month and year, e.g. 01/28"}
          inputProps={{
            maxLength: 5,
            inputMode: "numeric",
          }}
          sx={paymentTextField}
        />
      </Box>

      {/* CARD HOLDER NAME */}

      <Typography
        sx={{
          ...fieldLabelStyle,
          mt: 2,
        }}
      >
        Card Holder Name
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Enter card holder name"
        value={cardHolderName}
        onChange={(e) => setCardHolderName(e.target.value)}
        sx={paymentTextField}
      />

      {/* CVV */}

      <Typography
        sx={{
          ...fieldLabelStyle,
          mt: 2,
        }}
      >
        CVV/CVC
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Enter CVV"
        value={cvv}
        onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
        error={Boolean(errors?.cvv)}
        helperText={errors?.cvv}
        inputProps={{
          maxLength: 4,
          inputMode: "numeric",
        }}
        sx={paymentTextField}
      />
    </Box>
  );
};

// ======================================================
// CARD BRAND LOGOS
// ======================================================

const CardBrands = ({ type }) => {
  const brands =
    type === "debit"
      ? [
          "/assets/apspdcllogos/visalogo.png",
          "/assets/apspdcllogos/mastercard logo.png",
          "/assets/apspdcllogos/maestrocard logo.png",
          "/assets/apspdcllogos/rupay logo.png",
        ]
      : [
          "/assets/apspdcllogos/visalogo.png",
          "/assets/apspdcllogos/mastercard logo.png",
          "/assets/apspdcllogos/american expresslogo.png",
          "/assets/apspdcllogos/rupay logo.png",
        ];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 1,
        mb: 3,
        mt: 1,
        flexWrap: "wrap",
      }}
    >
      {brands.map((brand, index) => (
        <Box
          key={index}
          sx={{
            width: 70,
            height: 40,
            border: "1px solid #d5d5d5",
            borderRadius: "3px",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
          }}
        >
          <Box
            component="img"
            src={brand}
            alt="Card"
            sx={{
              maxWidth: "100%",
              maxHeight: 28,
              objectFit: "contain",
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

// ======================================================
// INTERNET BANKING
// ======================================================

const NetBankingForm = ({ selectedBank, setSelectedBank }) => {
  const banks = [
    {
      name: "Axis Bank",
      image: "/assets/apspdcllogos/axisbank logo.png",
      width: 75,
      height: 52,
    },
    {
      name: "HDFC Bank",
      image: "/assets/apspdcllogos/hdfclogo.png",
      width: 225,
      height: 45,
    },
    {
      name: "ICICI Bank",
      image: "/assets/apspdcllogos/icicibank logo.png",
      width: 125,
      height: 55,
    },
    {
      name: "Kotak Mahindra Bank",
      image: "/assets/apspdcllogos/kotakbank logo.png",
      width: 80,
      height: 35,
    },
    {
      name: "State Bank of India",
      image: "/assets/apspdcllogos/sbibank logo.png",
      width: 95,
      height: 35,
    },
  ];

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 20,
          color: "#000000",
          mb: 1.5,
        }}
      >
        Select your Bank
      </Typography>

      <Typography
        sx={{
          color: "red",
          fontSize: 16,
          mb: 2,
        }}
      >
        Popular Banks
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 2,
          maxWidth: 560,
        }}
      >
        {banks.map((bank, index) => (
          <Box
            key={index}
            sx={{
              width: 110,
              height: 55,
              border: "1px solid #d5d5d5",
              borderRadius: "4px",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
            }}
          >
            <Box
              component="img"
              src={bank.image}
              alt={bank.name}
              sx={{
                width: bank.width,
                height: bank.height,
                objectFit: "contain",
              }}
            />
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          color: "red",
          fontSize: 16,
          mt: 4,
          mb: 1,
        }}
      >
        All Banks
      </Typography>

      <FormControl fullWidth size="small">
        <Select
          displayEmpty
          value={selectedBank}
          onChange={(e) => setSelectedBank(e.target.value)}
          sx={{
            height: 48,
            fontSize: 18,
            borderRadius: "4px",
          }}
        >
          <MenuItem value="">== Select your Bank ==</MenuItem>

          {banks.map((bank) => (
            <MenuItem key={bank} value={bank}>
              {bank}
            </MenuItem>
          ))}

          <MenuItem value="Other Bank">Other Bank</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

// ======================================================
// BANK CARD
// ======================================================

const BankCard = ({ selected, onClick, index }) => {
  const bankStyles = [
    {
      color: "#b52b58",
      text: "AXIS BANK",
    },
    {
      color: "#003d8f",
      text: "HDFC BANK",
    },
    {
      color: "#9b3a25",
      text: "ICICI Bank",
    },
    {
      color: "#b01f44",
      text: "kotak",
    },
    {
      color: "#1e5aa8",
      text: "State Bank of India",
    },
  ];

  const style = bankStyles[index] || bankStyles[0];

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        height: 78,
        border: selected ? "2px solid #888" : "1px solid #dddddd",
        borderRadius: "3px",
        display: "flex",
        alignItems: "center",
        px: 1,
        cursor: "pointer",
        backgroundColor: "#ffffff",
      }}
    >
      <Radio
        checked={selected}
        size="small"
        sx={{
          color: "#777777",
          p: 0.5,

          "&.Mui-checked": {
            color: "#777777",
          },
        }}
      />

      <Typography
        sx={{
          color: style.color,
          fontWeight: 700,
          fontSize: index === 4 ? 10 : 15,
        }}
      >
        {style.text}
      </Typography>
    </Paper>
  );
};

// ======================================================
// WALLET / CASH CARD
// ======================================================

const WalletForm = ({ wallet, setWallet }) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 20,
          color: "#000000",
          mb: 1,
        }}
      >
        Pay using Wallet / Cash Card
      </Typography>

      <Typography
        sx={{
          fontSize: 16,
          color: "#777777",
          pb: 1.5,
          borderBottom: "1px solid #e5e5e5",
          mb: 3,
        }}
      >
        Please select your Wallet / Cash Card from the list below.
      </Typography>

      <FormControl fullWidth size="small">
        <Select
          value={wallet}
          displayEmpty
          onChange={(e) => setWallet(e.target.value)}
          sx={{
            height: 48,
            fontSize: 17,
          }}
        >
          <MenuItem value="">Wallet / Cash Card</MenuItem>

          <MenuItem value="Mobikwik">Mobikwik</MenuItem>

          <MenuItem value="Paytm">Paytm</MenuItem>

          <MenuItem value="Freecharge">Freecharge</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

// ======================================================
// MERCHANT SUMMARY
// ======================================================

const MerchantSummary = ({ paymentAmount }) => {
  return (
    <Box
      sx={{
        p: 1.2,
        pt: 1.5,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #6ec1dd",
          borderRadius: "2px",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        {/* MERCHANT */}

        <Box
          sx={{
            p: 1.2,
            minHeight: 145,
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: "#222222",
              mb: 1,
            }}
          >
            Merchant Name
          </Typography>

          <Typography
            sx={{
              fontSize: 18,
              lineHeight: 1.3,
              color: "#333333",
            }}
          >
            Andhra Pradesh Central
            <br />
            Power Distribution Corporation
            <br />
            Limited
          </Typography>
        </Box>

        {/* PAYMENT AMOUNT */}

        <Box
          sx={{
            backgroundColor: "#d9f1fa",
            borderTop: "1px solid #b5dce9",
            px: 1.2,
            py: 1.4,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 15,
              color: "#254b59",
            }}
          >
            Payment Amount:
          </Typography>

          <Typography
            component="span"
            sx={{
              ml: 1,
              fontSize: 17,
              fontWeight: 700,
              color: "#222222",
            }}
          >
            ₹ {Number(paymentAmount || 0).toFixed(2)}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

// ======================================================
// BILLDESK LOGO
// ======================================================

const BillDeskLogo = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.8,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          border: "4px solid #f58220",
          transform: "rotate(30deg)",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 10,
            height: 10,
            border: "3px solid #777777",
            left: 5,
            top: 5,
          }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          color: "#666666",
          letterSpacing: "-1px",
        }}
      >
        BillDesk
      </Typography>
    </Box>
  );
};

// ======================================================
// CARD NUMBER FORMAT
// ======================================================

const formatCardNumber = (value) => {
  const numbers = value.replace(/\D/g, "").slice(0, 16);

  return numbers.replace(/(.{4})/g, "$1 ").trim();
};

// ======================================================
// STYLES
// ======================================================

const fieldLabelStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: "#222222",
  mb: 0.7,
};

const paymentTextField = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "3px",
    height: 48,
    fontSize: 17,
    backgroundColor: "#ffffff",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cccccc",
  },

  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#999999",
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#999999",
    borderWidth: "1px",
  },

  "& input::placeholder": {
    opacity: 1,
    color: "#777777",
  },
};

export default TAWalletPayment;
